/**
 * Equipment Manager 的器材模板注册表 —— 从 `type` 创建器材实例。
 * 对应技术设计 §4.3（器材分类）、§4.4（器材对象示例）、§5.2（端口与槽位表）。
 *
 * 【素材策略】geometry 是内联几何图元（任务 051 决策 Q2）。它只是**画法**，
 * 端口锚点 `Port.anchor` 与几何用同一套器材局部坐标系，是真实可用的连接点。
 * 将来换成美术出的 SVG 素材时，只改这里的 `geometry`，EOM / Scene / 引擎接口零改动。
 */

import type { Capability, EquipmentCategory, LabObject, Pose } from "./lab-object.js";
import type { AnchorFacet, Port, Rect, Slot } from "./port.js";

/**
 * 可按器材状态求值的字段。
 *
 * 【为什么需要】§9「现象由状态驱动」与 §22 红线 4「实验现象由状态机触发，
 * 不能把全流程做成不可交互的视频」。静态图元画不出液面高低、火焰有无、瓶内变亮，
 * 于是「点燃酒精灯」只有一句文案没有画面 —— 见 `bug/023-化学/002-bug.md` 第 3 条。
 *
 * 求值函数只读 `object.state`，不碰图、不碰全局，所以换美术素材时仍然只改 `geometry`
 * （051 决策 Q2 的承诺不破）。
 */
export type Reactive<T> = T | ((state: Readonly<Record<string, unknown>>) => T);

export function resolveReactive<T>(
  value: Reactive<T> | undefined,
  state: Readonly<Record<string, unknown>>,
): T | undefined {
  return typeof value === "function" ? (value as (s: typeof state) => T)(state) : value;
}

export interface ShapeStyle {
  fill?: Reactive<string>;
  stroke?: string;
  strokeWidth?: number;
  opacity?: Reactive<number>;
  dash?: string;
  /** false 时整个图元不绘制。现象的开关就靠它 */
  visible?: Reactive<boolean>;
}

export type Shape =
  | ({ kind: "rect"; x: number; y: number; width: number; height: number; rx?: number } & ShapeStyle)
  | ({ kind: "ellipse"; cx: number; cy: number; rx: number; ry: number } & ShapeStyle)
  | ({ kind: "line"; x1: number; y1: number; x2: number; y2: number } & ShapeStyle)
  | ({ kind: "path"; d: string } & ShapeStyle)
  | ({ kind: "polygon"; points: string } & ShapeStyle)
  | ({ kind: "text"; x: number; y: number; content: Reactive<string>; fontSize?: number } & ShapeStyle)
  /** 液面：在 bounds 里从底部往上填到 level（0-1）。集气瓶排水、试管液面都用它 */
  | ({ kind: "liquid"; bounds: Rect; level: Reactive<number> } & ShapeStyle)
  /** 火焰：酒精灯点燃、燃烧类实验共用 */
  | ({ kind: "flame"; x: number; y: number; size?: Reactive<number> } & ShapeStyle)
  /** 气泡：在 bounds 内上浮。气密性检查、排水集气、金属与酸反应都用它 */
  | ({ kind: "bubbles"; bounds: Rect; rate: Reactive<number> } & ShapeStyle);

export interface EquipmentTemplate {
  type: string;
  name: string;
  category: EquipmentCategory;
  capabilities: Capability[];
  ports: Port[];
  slots: Slot[];
  defaultState: Record<string, unknown>;
  /** 器材局部包围盒，供 LayoutEngine 排布与命中测试使用 */
  bounds: Rect;
  /** 缺省所在区域：可搬动的小器材在托盘，固定装置在实验桌 */
  defaultZone: string;
  geometry: Shape[];
}

const GLASS: ShapeStyle = { fill: "#e8f4fb", stroke: "#5b8ba8", strokeWidth: 2, opacity: 0.92 };
const METAL: ShapeStyle = { fill: "#c9ced6", stroke: "#6b727d", strokeWidth: 2 };
const RUBBER: ShapeStyle = { fill: "#8a8f98", stroke: "#4d525a", strokeWidth: 2 };
const WATER: ShapeStyle = { fill: "#bfe3f5", stroke: "#4f93b5", strokeWidth: 2, opacity: 0.8 };

/**
 * 试管。局部坐标系按技术设计 §4.4 的示例对齐：横置，管口在右端 `{ x: 96, y: 20 }`。
 * 端口 body / bottom 是 §5.1 装配图查询用到的锚点（夹持、加热）。
 */
const TEST_TUBE: EquipmentTemplate = {
  type: "TestTube",
  name: "试管",
  category: "Container",
  capabilities: ["movable", "rotatable", "heatable", "container", "clampable", "pressurizable"],
  ports: [
    { id: "mouth", kind: "opening", label: "试管口", anchor: { x: 96, y: 20 }, radius: 28, direction: "out", accepts: ["cork.sealRing"] },
    { id: "body", kind: "clamp", label: "管身", anchor: { x: 52, y: 20 }, radius: 26, direction: "both", accepts: ["iron_stand.clamp"] },
    { id: "bottom", kind: "heat_target", label: "管底", anchor: { x: 12, y: 20 }, radius: 26, direction: "in", accepts: ["flame"] },
  ],
  slots: [
    {
      id: "inside",
      kind: "reagent_container",
      anchor: { x: 40, y: 20 },
      bounds: { x: 6, y: 8, width: 80, height: 24 },
      accepts: ["reagent.solid", "reagent.liquid"],
    },
    {
      id: "clamp_zone",
      kind: "clamp_target",
      anchor: { x: 52, y: 20 },
      bounds: { x: 36, y: 4, width: 34, height: 32 },
      accepts: ["iron_stand.clamp"],
    },
  ],
  defaultState: {
    temperatureC: 25,
    pressureKPa: 101.3,
    sealed: false,
    contains: [],
    mouthDirection: "right_down",
    isClamped: false,
  },
  bounds: { x: 0, y: 0, width: 100, height: 40 },
  defaultZone: "tray",
  geometry: [
    { kind: "path", d: "M20 2 H96 V38 H20 A18 18 0 0 1 20 2 Z", ...GLASS },
    // 药品：加进去才出现，颜色由实验包写在 state.solidColor（引擎不认识"高锰酸钾"）
    {
      kind: "rect",
      x: 16,
      y: 22,
      width: 44,
      height: 14,
      rx: 4,
      visible: (s) => s.hasReagent === true,
      fill: (s) => (typeof s.solidColor === "string" ? s.solidColor : "#5b3a72"),
      stroke: "none",
    },
    // 棉花团：塞在管口内侧
    {
      kind: "ellipse",
      cx: 80,
      cy: 20,
      rx: 9,
      ry: 11,
      visible: (s) => s.hasCotton === true,
      fill: "#f7f4ec",
      stroke: "#d8d2c4",
      strokeWidth: 1.5,
    },
    // 受热发红：温度越高越明显
    {
      kind: "rect",
      x: 4,
      y: 4,
      width: 34,
      height: 32,
      rx: 12,
      visible: (s) => Number(s.temperatureC ?? 0) > 60,
      fill: "#e2643a",
      stroke: "none",
      opacity: (s) => Math.min(0.5, (Number(s.temperatureC ?? 0) - 60) / 500),
    },
    { kind: "line", x1: 96, y1: 2, x2: 96, y2: 38, stroke: "#3f6d88", strokeWidth: 3 },
  ],
};

/** 棉花。§4.3 Consumable 族（滤纸 / pH试纸 / 木条 / 火柴 / 棉花），跨实验复用。 */
const COTTON: EquipmentTemplate = {
  type: "Cotton",
  name: "棉花",
  category: "Consumable",
  capabilities: ["movable"],
  ports: [
    {
      id: "body",
      kind: "solid_item",
      label: "棉花团",
      anchor: { x: 15, y: 14 },
      radius: 24,
      direction: "out",
      accepts: ["container.inside"],
    },
  ],
  slots: [],
  defaultState: { placed: false },
  bounds: { x: 0, y: 0, width: 30, height: 28 },
  defaultZone: "tray",
  geometry: [
    { kind: "ellipse", cx: 15, cy: 14, rx: 13, ry: 11, fill: "#f7f4ec", stroke: "#cfc8b8", strokeWidth: 2 },
    { kind: "ellipse", cx: 10, cy: 11, rx: 5, ry: 4, fill: "#fffdf8", stroke: "none" },
    { kind: "ellipse", cx: 20, cy: 17, rx: 4, ry: 3, fill: "#fffdf8", stroke: "none" },
  ],
};

/** 橡胶塞。端口 tubeHole 插导管、sealRing 密封试管口（§5.2）。 */
const CORK: EquipmentTemplate = {
  type: "Cork",
  name: "橡胶塞",
  category: "Connector",
  capabilities: ["movable", "connector"],
  ports: [
    { id: "sealRing", kind: "opening", label: "塞口", anchor: { x: 4, y: 14 }, radius: 24, direction: "in", accepts: ["test_tube.mouth", "flask.mouth"] },
    { id: "tubeHole", kind: "gas_out", label: "导管孔", anchor: { x: 26, y: 14 }, radius: 22, direction: "out", accepts: ["rubber_tube.input"] },
  ],
  slots: [],
  defaultState: { insertedInto: null },
  bounds: { x: 0, y: 0, width: 30, height: 28 },
  defaultZone: "tray",
  geometry: [
    { kind: "polygon", points: "4,2 26,6 26,22 4,26", ...RUBBER },
    { kind: "line", x1: 26, y1: 14, x2: 30, y2: 14, stroke: "#4d525a", strokeWidth: 3 },
  ],
};

/** 导管。input 接反应容器、output 接集气容器，path 是中段（§5.2 / §5.5 贝塞尔重绘）。 */
const RUBBER_TUBE: EquipmentTemplate = {
  type: "RubberTube",
  name: "导管",
  category: "Connector",
  capabilities: ["movable", "connector"],
  ports: [
    { id: "input", kind: "tube_end", label: "进气端", anchor: { x: 2, y: 8 }, radius: 24, direction: "in", accepts: ["cork.tubeHole"] },
    { id: "output", kind: "tube_end", label: "出气端", anchor: { x: 138, y: 76 }, radius: 24, direction: "out", accepts: ["gas_bottle.mouth", "water_tank.collectionZone"] },
    { id: "path", kind: "tube_path", label: "导管", anchor: { x: 70, y: 42 }, radius: 18, direction: "both", accepts: [] },
  ],
  slots: [],
  defaultState: { blocked: false, inWater: false },
  bounds: { x: 0, y: 0, width: 140, height: 84 },
  defaultZone: "tray",
  geometry: [
    { kind: "path", d: "M2 8 C 60 8, 80 20, 90 50 C 96 70, 118 76, 138 76", fill: "none", stroke: "#6f7681", strokeWidth: 6 },
    { kind: "path", d: "M2 8 C 60 8, 80 20, 90 50 C 96 70, 118 76, 138 76", fill: "none", stroke: "#a8aeb8", strokeWidth: 2 },
  ],
};

/** 集气瓶。排水法/排空气法收集（§5.2）。 */
const GAS_BOTTLE: EquipmentTemplate = {
  type: "GasBottle",
  name: "集气瓶",
  category: "Container",
  capabilities: ["movable", "container", "collectable", "pressurizable"],
  ports: [
    { id: "mouth", kind: "opening", label: "瓶口", anchor: { x: 35, y: 4 }, radius: 30, direction: "in", accepts: ["rubber_tube.output", "glass_plate"] },
  ],
  slots: [
    {
      id: "inside",
      kind: "gas_container",
      anchor: { x: 35, y: 60 },
      bounds: { x: 6, y: 14, width: 58, height: 82 },
      accepts: ["gas", "reagent.liquid"],
    },
  ],
  defaultState: { gasVolumeRatio: 0, gasId: null, sealed: false, pressureKPa: 101.3 },
  bounds: { x: 0, y: 0, width: 70, height: 100 },
  defaultZone: "tray",
  geometry: [
    { kind: "rect", x: 6, y: 8, width: 58, height: 88, rx: 6, ...GLASS },
    // 排水集气：瓶里的水随 gasVolumeRatio 下降，学生看得见「气把水顶下去」
    {
      kind: "liquid",
      bounds: { x: 8, y: 10, width: 54, height: 84 },
      level: (s) => 1 - Math.min(1, Math.max(0, Number(s.gasVolumeRatio ?? 0))),
      visible: (s) => Number(s.gasVolumeRatio ?? 0) > 0,
      fill: "#bfe3f5",
      opacity: 0.85,
    },
    // 集满后瓶内透亮
    {
      kind: "rect",
      x: 8,
      y: 10,
      width: 54,
      height: 84,
      rx: 4,
      visible: (s) => Number(s.gasVolumeRatio ?? 0) >= 0.95,
      fill: "#ffffff",
      stroke: "none",
      opacity: 0.55,
    },
    { kind: "line", x1: 6, y1: 8, x2: 64, y2: 8, stroke: "#3f6d88", strokeWidth: 3 },
  ],
};

/** 水槽。排水集气区（§5.2：waterArea / collectionZone）。 */
const WATER_TANK: EquipmentTemplate = {
  type: "WaterTank",
  name: "水槽",
  category: "Container",
  capabilities: ["container"],
  ports: [],
  slots: [
    {
      id: "waterArea",
      kind: "water_area",
      anchor: { x: 110, y: 70 },
      bounds: { x: 8, y: 34, width: 204, height: 68 },
      accepts: ["rubber_tube.output", "gas_bottle"],
    },
    {
      id: "collectionZone",
      kind: "water_collect_zone",
      anchor: { x: 150, y: 58 },
      bounds: { x: 110, y: 30, width: 96, height: 72 },
      accepts: ["gas_bottle"],
    },
  ],
  defaultState: { waterLevel: 0.62 },
  bounds: { x: 0, y: 0, width: 220, height: 110 },
  defaultZone: "water_area",
  geometry: [
    { kind: "rect", x: 2, y: 12, width: 216, height: 94, rx: 8, fill: "#eef6fa", stroke: "#5b8ba8", strokeWidth: 3 },
    { kind: "rect", x: 8, y: 34, width: 204, height: 68, rx: 4, ...WATER },
    { kind: "line", x1: 8, y1: 34, x2: 212, y2: 34, stroke: "#4f93b5", strokeWidth: 2, dash: "6 4" },
    // 气泡：由实验包的规则置 state.bubbling 决定快慢，引擎不认识"氧气"
    {
      kind: "bubbles",
      bounds: { x: 20, y: 40, width: 80, height: 58 },
      rate: (s) => Number(s.bubbling ?? 0),
      visible: (s) => Number(s.bubbling ?? 0) > 0,
    },
  ],
};

/** 酒精灯。flame 端口是 §5.1 `heat` 关系的起点。 */
const ALCOHOL_LAMP: EquipmentTemplate = {
  type: "AlcoholLamp",
  name: "酒精灯",
  category: "Tool",
  capabilities: ["movable", "ignitable"],
  ports: [
    { id: "flame", kind: "flame", label: "火焰", anchor: { x: 30, y: 6 }, radius: 30, direction: "out", accepts: ["heat_target"] },
    { id: "body", kind: "grip", label: "灯身", anchor: { x: 30, y: 62 }, radius: 24, direction: "both", accepts: [] },
  ],
  slots: [],
  defaultState: { lit: false, capOn: false, alcoholLevel: 0.7 },
  bounds: { x: 0, y: 0, width: 60, height: 92 },
  defaultZone: "workbench",
  geometry: [
    { kind: "path", d: "M10 40 Q10 30 22 26 H38 Q50 30 50 40 V84 Q50 90 44 90 H16 Q10 90 10 84 Z", fill: "#f2e2c4", stroke: "#a8875a", strokeWidth: 2 },
    { kind: "rect", x: 24, y: 16, width: 12, height: 12, rx: 2, ...METAL },
    { kind: "line", x1: 30, y1: 16, x2: 30, y2: 8, stroke: "#8a6a3a", strokeWidth: 3 },
    // 火焰：点燃才画。bug/023-化学/002-bug.md 第 3 条「如何点燃酒精灯？没有动作」——
    // 之前 lit 只改了一个字段，画面上毫无变化，学生当然以为没反应。
    { kind: "flame", x: 30, y: 6, visible: (s) => s.lit === true },
  ],
};

/** 铁架台。clamp 端口是 §5.1 `clamp` 关系的起点。 */
const IRON_STAND: EquipmentTemplate = {
  type: "IronStand",
  name: "铁架台",
  category: "Tool",
  capabilities: ["movable"],
  ports: [
    { id: "clamp", kind: "clamp", label: "铁夹", anchor: { x: 96, y: 70 }, radius: 32, direction: "out", accepts: ["test_tube.body", "funnel.body"] },
    { id: "base", kind: "place", label: "底座", anchor: { x: 40, y: 246 }, radius: 24, direction: "both", accepts: [] },
  ],
  slots: [],
  defaultState: { clampHeight: 70 },
  bounds: { x: 0, y: 0, width: 140, height: 254 },
  defaultZone: "workbench",
  geometry: [
    { kind: "rect", x: 4, y: 236, width: 118, height: 16, rx: 4, ...METAL },
    { kind: "rect", x: 32, y: 10, width: 12, height: 228, ...METAL },
    { kind: "line", x1: 44, y1: 70, x2: 92, y2: 70, stroke: "#6b727d", strokeWidth: 8 },
    { kind: "path", d: "M92 58 Q112 58 112 70 Q112 82 92 82", fill: "none", stroke: "#6b727d", strokeWidth: 7 },
  ],
};

/**
 * 试剂。用 substanceId 区分具体物质（§11.3 `{ id: "kmno4_1", type: "Reagent", substanceId: "kmno4" }`）。
 * P0 只做一个通用外观 + 物质标签；物质表与反应在 P2 的 logic/ 落地。
 */
const REAGENT: EquipmentTemplate = {
  type: "Reagent",
  name: "试剂",
  category: "Reagent",
  capabilities: ["movable", "pourable"],
  ports: [
    { id: "outlet", kind: "opening", label: "取用口", anchor: { x: 26, y: 6 }, radius: 22, direction: "out", accepts: ["container.inside"] },
  ],
  slots: [],
  defaultState: { substanceId: null, amountGram: 0, consumed: false },
  bounds: { x: 0, y: 0, width: 52, height: 70 },
  defaultZone: "tray",
  geometry: [
    { kind: "rect", x: 8, y: 12, width: 36, height: 54, rx: 5, fill: "#f6f1e6", stroke: "#9a8b6d", strokeWidth: 2 },
    { kind: "rect", x: 18, y: 4, width: 16, height: 10, rx: 2, ...RUBBER },
    { kind: "rect", x: 12, y: 40, width: 28, height: 22, rx: 3, fill: "#5b3a72", stroke: "none" },
  ],
};

const TEMPLATES: readonly EquipmentTemplate[] = [
  TEST_TUBE,
  COTTON,
  CORK,
  RUBBER_TUBE,
  GAS_BOTTLE,
  WATER_TANK,
  ALCOHOL_LAMP,
  IRON_STAND,
  REAGENT,
] as const;

const REGISTRY: ReadonlyMap<string, EquipmentTemplate> = new Map(
  TEMPLATES.map((template) => [template.type, template]),
);

export function getTemplate(type: string): EquipmentTemplate | undefined {
  return REGISTRY.get(type);
}

export function hasTemplate(type: string): boolean {
  return REGISTRY.has(type);
}

export function listTemplateTypes(): string[] {
  return [...REGISTRY.keys()];
}

/* ── 语义标签与可连接点（§5.2 / §5.3，红线 1 的判据来源，见 port.ts 注释） ── */

/** `TestTube` → `test_tube`、`RubberTube` → `rubber_tube`。tag 的第一段。 */
export function snakeCase(type: string): string {
  return type.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase();
}

/** 槽位没有 radius，由 bounds 推导：取短边一半，但不小于端口的建议下限。 */
const SLOT_MIN_RADIUS = 24;

function slotRadius(slot: Slot): number {
  return Math.max(SLOT_MIN_RADIUS, Math.min(slot.bounds.width, slot.bounds.height) / 2);
}

function tagsFor(
  objectType: string,
  category: EquipmentCategory,
  anchorId: string | null,
  kind: string | null,
): string[] {
  const type = snakeCase(objectType);
  if (anchorId === null) return [type];
  const tags = [`${type}.${anchorId}`, `${category.toLowerCase()}.${anchorId}`];
  if (kind !== null) tags.push(kind);
  return tags;
}

/**
 * 列出一件器材上所有可连接点：每个端口、每个槽位，外加「整件器材」本身
 * （拖整个集气瓶进水槽收集区就是整件器材对槽位，没有端口参与）。
 *
 * 槽位与整件器材恒为接收方（direction "in"）—— 方向决定 Relation 的 from/to，
 * 见 interaction/action-grammar.ts。
 */
export function describeAnchors(object: LabObject): AnchorFacet[] {
  const template = getTemplate(object.type);
  if (!template) return [];
  const category = template.category;

  const ports: AnchorFacet[] = (object.ports ?? []).map((port) => ({
    objectId: object.id,
    objectType: object.type,
    anchorId: port.id,
    anchorType: "port" as const,
    kind: port.kind,
    direction: port.direction ?? "both",
    anchor: port.anchor,
    radius: port.radius ?? SLOT_MIN_RADIUS,
    tags: tagsFor(object.type, category, port.id, port.kind),
    accepts: port.accepts,
  }));

  const slots: AnchorFacet[] = (object.slots ?? []).map((slot) => ({
    objectId: object.id,
    objectType: object.type,
    anchorId: slot.id,
    anchorType: "slot" as const,
    kind: slot.kind,
    direction: "in" as const,
    anchor: slot.anchor,
    radius: slotRadius(slot),
    tags: tagsFor(object.type, category, slot.id, slot.kind),
    accepts: slot.accepts,
  }));

  const whole: AnchorFacet = {
    objectId: object.id,
    objectType: object.type,
    anchorId: null,
    anchorType: "object",
    kind: null,
    direction: "in",
    anchor: { x: template.bounds.width / 2, y: template.bounds.height / 2 },
    radius: Math.max(template.bounds.width, template.bounds.height) / 2,
    tags: tagsFor(object.type, category, null, null),
    accepts: [],
  };

  return [...ports, ...slots, whole];
}

const DEFAULT_POSE: Pose = { x: 0, y: 0, rotation: 0, scale: 1, zIndex: 0 };

export interface InstantiateOptions {
  id: string;
  type: string;
  name?: string;
  substanceId?: string;
  pose?: Partial<Pose>;
  state?: Record<string, unknown>;
}

/**
 * 由模板创建器材实例。实验包只写 `{ id, type }` 时，name/ports/slots/state 全部由模板补齐；
 * 实验包给了哪一项就覆盖哪一项（§11.3 的最小写法与 §4.4 的完整写法都要成立）。
 */
export function instantiate(options: InstantiateOptions): LabObject {
  const template = getTemplate(options.type);
  if (!template) {
    throw new Error(
      `未知器材类型 "${options.type}"（器材 ${options.id}）。可用类型：${listTemplateTypes().join(", ")}`,
    );
  }

  const state: Record<string, unknown> = { ...template.defaultState, ...options.state };
  if (options.substanceId !== undefined) {
    state.substanceId = options.substanceId;
  }

  return {
    id: options.id,
    type: template.type,
    name: options.name ?? template.name,
    pose: { ...DEFAULT_POSE, ...options.pose },
    state,
    // ports / slots / capabilities 是模板的静态几何与声明，实例间共享同一份引用。
    // 全工程按不可变方式对待它们（要改就换新数组），不要就地 push/splice。
    ports: template.ports,
    slots: template.slots,
    capabilities: template.capabilities,
  };
}
